import { type Dispatch, type SetStateAction, useEffect } from 'react';

import { Box, type SelectProps, Text } from '@chakra-ui/react';
import type { MenuPlacement, OptionBase } from 'chakra-react-select';
import { useTranslation } from 'react-i18next';

import { Select } from '@/components/Select';
import { useGetReminderCallSettingsTemplatesList } from '@/features/settings/reminder-call/queries';
import { type SmsTemplateDto } from '@/services/api';

interface TemplateOptions extends OptionBase {
  label: string;
  value: string | undefined;
}

type CallTemplateSelectProps = {
  selectedTemplateId: SmsTemplateDto['id'] | undefined;
  setSelectedTemplateId: Dispatch<
    SetStateAction<SmsTemplateDto['id'] | undefined>
  >;
  showLabel?: boolean;
  isOptional?: boolean;
  menuPlacement?: MenuPlacement;
  size?: SelectProps['size'];
};

export const CallTemplateSelect = ({
  selectedTemplateId,
  setSelectedTemplateId,
  showLabel,
  isOptional,
  menuPlacement,
  size,
}: CallTemplateSelectProps) => {
  const { t } = useTranslation(['components', 'common']);

  const list = useGetReminderCallSettingsTemplatesList();

  const toOptions = (template: SmsTemplateDto): TemplateOptions => {
    return {
      label: template.title!,
      value: template.id!,
    };
  };

  useEffect(() => {
    if (list.data && !selectedTemplateId && !isOptional) {
      // Set the first template as the default if none is selected
      const defaultTemplateId = list.data[0]?.id;
      if (defaultTemplateId) {
        setSelectedTemplateId(defaultTemplateId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, selectedTemplateId]);

  useEffect(() => {
    return () => {
      if (!isOptional) {
        setSelectedTemplateId(undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box w="full">
      {showLabel && (
        <Text fontSize="sm" color="gray.700" fontWeight="medium" mb={1}>
          {t('components:reminderCallSettings.reminderCallTemplate')}
        </Text>
      )}
      <Select
        size={size as ExplicitAny}
        isLoading={list.isLoading}
        placeholder={t('common:actions.select')}
        value={list.data
          ?.map(toOptions)
          .find((template) => template.value === selectedTemplateId)}
        onChange={(template) => {
          if (template) setSelectedTemplateId(template.value);
          console.log({ template });
        }}
        options={[...(list.data?.map(toOptions) ?? [])]}
        menuPlacement={menuPlacement}
      />
    </Box>
  );
};
